import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Stack,
  alpha,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  EmailOutlined,
  LockOutlined,
  LoginOutlined,
  Nfc as NfcIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BRAND } from "../../constants/brand";
import PoweredByBrand from "../../components/brand/PoweredByBrand";
import { useAuth } from "../../contexts/AuthContext";
import "./Login.css";

const schema = yup.object({
  email: yup.string().email("Invalid email format").required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

type FormData = yup.InferType<typeof schema>;

const FEATURES = [
  { icon: <NfcIcon fontSize="small" />, text: "NFC cards & quick recharge" },
  { icon: <CreditCardIcon fontSize="small" />, text: "Sales, inventory & reports" },
  { icon: <SecurityIcon fontSize="small" />, text: "Secure role-based access" },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [requires2fa, setRequires2fa] = React.useState(false);
  const [totpCode, setTotpCode] = React.useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: FormData) => {
    setError("");
    setIsLoading(true);
    try {
      const result = await login(
        data.email,
        data.password,
        requires2fa ? totpCode : undefined
      );
      if (result.requires2fa) {
        setRequires2fa(true);
        setError("");
        return;
      }
      navigate("/dashboard");
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Login failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      bgcolor: alpha("#FFFFFF", 0.9),
      transition: "box-shadow 0.2s, border-color 0.2s",
      "&:hover": { bgcolor: "#FFFFFF" },
      "&.Mui-focused": {
        bgcolor: "#FFFFFF",
        boxShadow: `0 0 0 3px ${alpha(BRAND.colors.orange500, 0.2)}`,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: BRAND.colors.orange600,
    },
  };

  const footerLinkSx = {
    color: BRAND.colors.orange700,
    fontWeight: 700,
    textTransform: "none",
    textDecoration: "none",
    cursor: "pointer",
    position: "relative",
    zIndex: 5,
    minWidth: "auto",
    px: 1,
    "&:hover": {
      bgcolor: alpha(BRAND.colors.orange500, 0.1),
      textDecoration: "underline",
    },
  } as const;

  return (
    <Box
      className="login-page"
      sx={{
        minHeight: "100vh",
        display: "flex",
        position: "relative",
        overflow: "hidden",
        bgcolor: "#FFFFFF",
      }}
    >
      {/* Brand column — light so logo stays visible */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          p: 6,
          position: "relative",
          zIndex: 1,
          bgcolor: "#FFFFFF",
          borderRight: `1px solid ${alpha(BRAND.colors.orange500, 0.12)}`,
        }}
      >
        <Box sx={{ maxWidth: 400, width: "100%" }}>
          <Box
            component="img"
            src={BRAND.logo.large}
            alt={BRAND.name}
            sx={{
              maxWidth: "100%",
              maxHeight: 200,
              objectFit: "contain",
              mb: 3,
            }}
          />
          <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }} color="text.primary">
            {BRAND.productNameAdmin}
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="primary.main"
            sx={{ mb: 0.5 }}
          >
            {BRAND.tagline}
          </Typography>
          <Typography
            variant="h6"
            fontWeight={600}
            color="text.secondary"
            sx={{ mb: 3, lineHeight: 1.45 }}
          >
            Manage cards, recharges, and branch operations from one admin portal.
          </Typography>
          <Stack spacing={1.5}>
            {FEATURES.map((f) => (
              <Stack key={f.text} direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: alpha(BRAND.colors.orange500, 0.12),
                    color: BRAND.colors.orange600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {f.icon}
                </Box>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {f.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Form column — orange design only here */}
      <Box
        className="login-form-decor"
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          p: { xs: 2, sm: 4 },
          overflow: "hidden",
          background: BRAND.loginPanelGradient,
        }}
      >
        <Box
          aria-hidden
          className="login-curve-layer"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 720 900"
            preserveAspectRatio="xMidYMid slice"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <path
              d="M0,0 C180,120 280,280 360,450 C440,620 520,780 720,900 L720,0 Z"
              fill={alpha("#FFFFFF", 0.08)}
            />
            <path
              d="M0,720 C180,640 360,820 540,700 C630,640 680,760 720,820 L720,900 L0,900 Z"
              fill={alpha(BRAND.colors.orange100, 0.35)}
            />
            <path
              d="M400,0 C520,40 620,120 720,220 L720,0 Z"
              fill={alpha("#FFFFFF", 0.12)}
            />
          </Box>
        </Box>

        <Paper
          elevation={0}
          className="login-form-card"
          sx={{
            p: { xs: 3, sm: 4 },
            width: "100%",
            maxWidth: 440,
            borderRadius: 4,
            position: "relative",
            zIndex: 1,
            bgcolor: alpha("#FFFFFF", 0.92),
            backdropFilter: "blur(14px)",
            border: `1px solid ${alpha("#FFFFFF", 0.55)}`,
            boxShadow: `0 1px 0 ${alpha("#FFFFFF", 0.95)} inset, 0 18px 48px ${alpha(
              BRAND.colors.navy,
              0.18
            )}`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${BRAND.colors.orange400}, ${BRAND.colors.orange600}, ${BRAND.colors.orange400})`,
            },
          }}
        >
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Box textAlign="center">
              <Box
                component="img"
                src={BRAND.logo.md}
                alt={BRAND.name}
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: "contain",
                  mb: 1.5,
                  display: { md: "none" },
                  mx: "auto",
                }}
              />
              <Typography
                variant="h4"
                fontWeight={800}
                color="text.primary"
                sx={{ letterSpacing: "-0.02em" }}
              >
                Welcome back
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {BRAND.productNameAdmin} · Sign in to continue
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2.25}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      label="Email address"
                      autoComplete="email"
                      autoFocus
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      label="Password"
                      type="password"
                      autoComplete="current-password"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  )}
                />
              </Stack>

              {requires2fa && (
                <TextField
                  fullWidth
                  label="Authenticator code"
                  value={totpCode}
                  onChange={(e) =>
                    setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  helperText="Enter the 6-digit code from your authenticator app"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
                  sx={{ ...inputSx, mt: 2.25 }}
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <LoginOutlined />
                  )
                }
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: "1rem",
                  textTransform: "none",
                  bgcolor: BRAND.colors.orange600,
                  boxShadow: `0 4px 16px ${alpha(BRAND.colors.orange600, 0.4)}`,
                  "&:hover": {
                    bgcolor: BRAND.colors.orange700,
                    boxShadow: `0 8px 24px ${alpha(BRAND.colors.orange700, 0.35)}`,
                  },
                  "&.Mui-disabled": {
                    bgcolor: alpha(BRAND.colors.orange500, 0.6),
                    color: "#FFFFFF",
                  },
                }}
              >
                {isLoading ? "Signing in…" : "Sign in"}
              </Button>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
              spacing={1}
              useFlexGap
              sx={{ pt: 1, position: "relative", zIndex: 5 }}
            >
              <Button
                component={RouterLink}
                to="/"
                size="small"
                sx={footerLinkSx}
              >
                Home
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                size="small"
                variant="text"
                sx={footerLinkSx}
              >
                Register company
              </Button>
              <Button
                component={RouterLink}
                to="/privacy-policy"
                size="small"
                variant="text"
                sx={footerLinkSx}
              >
                Privacy Policy
              </Button>
            </Stack>

            <PoweredByBrand sx={{ pt: 0.5 }} />
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;
